import { IMaintenance } from '@alberta/konexi-shared';
import { inject, ModuleWithProviders, NgModule, NgZone } from '@angular/core';
import { IMaintenanceService } from 'src/app/business/maintenance/contracts/maintenance';
import { IAppController } from 'src/app/common/contracts/controller/app-controller';
import { IDispatcher } from 'src/app/common/contracts/dispatch/dispatcher';
import { IPlatformSync, PlatformSyncToken } from 'src/app/common/contracts/sync/platform-sync';
import { AppController } from 'src/app/common/controller/app-controller';
import { Dispatcher } from 'src/app/common/dispatch/dispatcher';
import { ReturnDeliverySyncService } from 'src/app/shared/services/sync/return-delivery-sync.service';

import { SyncProgressEvent } from '../../models/sync-progress-event';
import { EventService } from '../event.service';
import { MaintenanceService } from '../maintenance/maintenance.service';
import { AppointmentSyncService } from './appointment-sync.service';
import { ArticleSyncService } from './article-sync.service';
import { AttachmentSyncService } from './attachment-sync.service';
import { AttachmentTypeSyncService } from './attachment-type-sync.service';
import { AuditSyncService } from './audit-sync.service';
import { CalendarResourceSyncService } from './calendar-resource-sync.service';
import { CareProposalSyncService } from './care-proposal-sync.service';
import { ConfigSyncService } from './config-sync.service';
import { DeviceSyncService } from './device-sync.service';
import { DirectOrderSyncService } from './direct-order-sync.service';
import { DoctorSyncService } from './doctor-sync.service';
import { ErpOrderSyncService } from './erp-order-sync.service';
import { ErpTaskSyncService } from './erp-task-sync.service';
import { FaxSyncService } from './fax-sync.service';
import { GeneralSyncService } from './general-sync.service';
import { GroupSyncService } from './group-sync.service';
import { HospitalSyncService } from './hosptial-sync.service';
import { InstitutionAttributeSyncService } from './institution-attribute-sync.service';
import { InstitutionAuditSyncService } from './institution-audit-sync.service';
import { InstitutionContactSyncService } from './institution-contact-sync.service';
import { InstitutionNoteSyncService } from './institution-note-sync.service';
import { InsuranceContractSyncService } from './insurance-contract-sync.service';
import { IntegratedCareReturnDeliverySyncService } from './integrated-care-return-delivery-sync.service';
import { IntegratedCareSyncService } from './integrated-care-sync.service';
import { KiwiInstitutionSyncService } from './kiwi-institution-sync.service';
import { LoggingSyncService } from './logging-sync.service';
import { MaintenanceSyncService } from './maintenance-sync.service';
import { MedicalOrderRequestSyncService } from './medical-order-request-sync.service';
import { MedicalOrderSyncService } from './medical-order-sync.service';
import { NursingHomeSyncService } from './nursing-home.service';
import { NursingServiceSyncService } from './nursing-service-sync.service';
import { OrderSyncService } from './order-sync.service';
import { PatientAppUserSyncService } from './patient-app-user-sync.service';
import { PatientSyncService } from './patient-sync.service';
import { PatientNoteSyncService } from './patientNotes-sync.service';
import { PharmacySyncService } from './pharmacy-sync.service';
import { PlannedOrderSyncService } from './planned-order-sync.service';
import { PortalNotificationSyncService } from './portal-notification-sync.service';
import { PrescriptionRequestSyncService } from './prescription-request-sync.service';
import { ProductGroupSyncService } from './product-group-sync.service';
import { RegionSyncService } from './region-sync.service';
import { ReminderSyncService } from './reminder-sync.service';
import { ServiceApprovalSyncService } from './service-approval-sync.service';
import { ServiceCostEstimateSyncService } from './service-cost-estimate-sync.service';
import { ServiceSyncService } from './service-sync.service';
import { ServiceTaskSyncService } from './service-task-sync.service';
import { SingleOrderSyncService } from './single-order-sync.service';
import { StandardCareProposalSyncService } from './standard-care-proposal-sync.service';
import { SubunitSyncService } from './subunit-sync.service';
import { SyncService } from './sync.service';
import { UsersSyncService } from './users-sync.service';
import { WebPatientAppUserSyncService } from './web-patient-app-user-sync.service';
import { WebSyncService } from './web-sync.service';
import { ProofOfServiceSyncService } from './proof-of-service-sync.service';
import { CooperationPartnerSyncService } from './cooperation-partner-sync.service';
// [schematics import marker create-entity]

let maintenanceSyncService: MaintenanceSyncService = null;

export function maintenanceSyncServiceFactory(
  appController: IAppController,
  dispatcher: IDispatcher<IMaintenance>,
  eventService: EventService<SyncProgressEvent>,
  maintenanceService: IMaintenanceService,
  ngZone: NgZone
) {
  if (maintenanceSyncService) {
    return maintenanceSyncService;
  }

  maintenanceSyncService = new MaintenanceSyncService(
    appController,
    dispatcher,
    eventService,
    maintenanceService,
    ngZone,
    inject<IPlatformSync>(PlatformSyncToken)
  );

  return maintenanceSyncService;
}
@NgModule({
  providers: [],
})
export class SyncModule {
  static forChild(): ModuleWithProviders<SyncModule> {
    return { ngModule: SyncModule, providers: [] };
  }

  static forRoot(): ModuleWithProviders<SyncModule> {
    return {
      ngModule: SyncModule,
      providers: [
        { provide: SyncService, useClass: ArticleSyncService, multi: true },
        { provide: SyncService, useClass: PatientSyncService, multi: true },
        { provide: SyncService, useClass: AuditSyncService, multi: true },
        { provide: SyncService, useClass: GeneralSyncService, multi: true },
        { provide: SyncService, useClass: UsersSyncService, multi: true },
        { provide: SyncService, useClass: GroupSyncService, multi: true },
        { provide: SyncService, useClass: RegionSyncService, multi: true },
        { provide: SyncService, useClass: ProductGroupSyncService, multi: true },
        { provide: SyncService, useClass: StandardCareProposalSyncService, multi: true },
        { provide: SyncService, useClass: CareProposalSyncService, multi: true },
        { provide: SyncService, useClass: InsuranceContractSyncService, multi: true },
        { provide: SyncService, useClass: OrderSyncService, multi: true },
        { provide: SyncService, useClass: SingleOrderSyncService, multi: true },
        { provide: SyncService, useClass: PatientNoteSyncService, multi: true },
        { provide: SyncService, useClass: FaxSyncService, multi: true },
        { provide: SyncService, useClass: LoggingSyncService, multi: true },
        { provide: SyncService, useClass: ErpOrderSyncService, multi: true },
        { provide: SyncService, useClass: DeviceSyncService, multi: true },
        { provide: SyncService, useClass: AttachmentSyncService, multi: true },
        { provide: SyncService, useClass: AttachmentTypeSyncService, multi: true },
        { provide: SyncService, useClass: DoctorSyncService, multi: true },
        { provide: SyncService, useClass: NursingHomeSyncService, multi: true },
        { provide: SyncService, useClass: HospitalSyncService, multi: true },
        { provide: SyncService, useClass: PharmacySyncService, multi: true },
        { provide: SyncService, useClass: NursingServiceSyncService, multi: true },
        {
          provide: SyncService,
          useFactory: maintenanceSyncServiceFactory,
          deps: [AppController, Dispatcher, EventService, MaintenanceService, NgZone],
          multi: true,
        },
        { provide: SyncService, useClass: SubunitSyncService, multi: true },
        { provide: SyncService, useClass: InstitutionContactSyncService, multi: true },
        { provide: SyncService, useClass: ServiceSyncService, multi: true },
        { provide: SyncService, useClass: ServiceTaskSyncService, multi: true },
        { provide: SyncService, useClass: CalendarResourceSyncService, multi: true },
        {
          provide: MaintenanceSyncService,
          useFactory: maintenanceSyncServiceFactory,
          deps: [AppController, Dispatcher, EventService, MaintenanceService, NgZone],
        },
        { provide: SyncService, useClass: InstitutionNoteSyncService, multi: true },
        { provide: SyncService, useClass: InstitutionAuditSyncService, multi: true },
        { provide: SyncService, useClass: ReturnDeliverySyncService, multi: true },
        { provide: SyncService, useClass: ConfigSyncService, multi: true },
        { provide: SyncService, useClass: AppointmentSyncService, multi: true },
        { provide: SyncService, useClass: PatientAppUserSyncService, multi: true },
        { provide: SyncService, useClass: ErpTaskSyncService, multi: true },
        { provide: SyncService, useClass: IntegratedCareSyncService, multi: true },
        { provide: SyncService, useClass: DirectOrderSyncService, multi: true },
        { provide: SyncService, useClass: ReminderSyncService, multi: true },
        { provide: SyncService, useClass: PlannedOrderSyncService, multi: true },
        { provide: SyncService, useClass: IntegratedCareReturnDeliverySyncService, multi: true },
        { provide: SyncService, useClass: InstitutionAttributeSyncService, multi: true },
        { provide: SyncService, useClass: PrescriptionRequestSyncService, multi: true },
        { provide: SyncService, useClass: MedicalOrderRequestSyncService, multi: true },
        { provide: SyncService, useClass: ServiceApprovalSyncService, multi: true },
        { provide: SyncService, useClass: MedicalOrderSyncService, multi: true },
        { provide: SyncService, useClass: ServiceCostEstimateSyncService, multi: true },
        { provide: SyncService, useClass: PortalNotificationSyncService, multi: true },
        { provide: WebSyncService, useClass: WebPatientAppUserSyncService, multi: true },
        { provide: SyncService, useClass: KiwiInstitutionSyncService, multi: true },
        { provide: SyncService, useClass: ProofOfServiceSyncService, multi: true },
        { provide: SyncService, useClass: CooperationPartnerSyncService, multi: true },
        // [schematics sync module marker create-entity]
      ],
    };
  }
}
